import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import {
  Button, Input, Modal, Upload, message, Table, Space, Form, Popconfirm, Select
} from 'antd';
import {
  UploadOutlined, PlusOutlined, EyeOutlined,
  EditOutlined, DeleteOutlined
} from '@ant-design/icons';
import './categoryManager.css';

const CategoryManager = () => {
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch categories
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const [parentsRes, allRes] = await Promise.all([
        axiosInstance.get('/categories/all-parent'),
        axiosInstance.get('/categories')
      ]);
      setParentCategories(parentsRes.data);
      setCategories(allRes.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error("Failed to fetch categories");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Submit category
  const handleFormSubmit = async (values) => {
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      if (values.description) formData.append('description', values.description);
      if (values.parentCategory) formData.append('parentCategory', values.parentCategory);
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('image', fileList[0].originFileObj);
      }

      if (editingCategory) {
        await axiosInstance.put(`/admin/categories/${editingCategory._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Category updated successfully');
      } else {
        await axiosInstance.post('/admin/categories', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Category created successfully');
      }

      setIsModalOpen(false);
      form.resetFields();
      setEditingCategory(null);
      setFileList([]);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      message.error(error?.response?.data?.message || 'Error saving category');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Edit category
  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name: category.name,
      description: category.description,
      parentCategory: category.parentCategory?._id
    });

    if (category.image?.url) {
      setFileList([{
        uid: '-1',
        name: category.image.fileName || 'image.png',
        status: 'done',
        url: category.image.url
      }]);
    } else {
      setFileList([]);
    }
    setIsModalOpen(true);
  };

  // Delete category
  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/admin/categories/${id}`);
      message.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      message.error(error?.response?.data?.message || 'Error deleting category');
    }
  };

  // View category
  const handleView = async (id) => {
    try {
      const res = await axiosInstance.get(`/categories/${id}`);
      Modal.info({
        title: `Category Details - ${res.data.name}`,
        content: (
          <div>
            <p><b>Slug:</b> {res.data.slug}</p>
            <p><b>Description:</b> {res.data.description || 'No description'}</p>
            <p><b>Parent:</b> {res.data.parentCategory?.name || 'None (Parent Category)'}</p>
            <p><b>Created By:</b> {res.data.createdBy || 'Unknown'}</p>
            {res.data.image?.url && (
              <div style={{ marginTop: 10 }}>
                <p><b>Image:</b></p>
                <img src={res.data.image.url} alt="category" style={{ maxWidth: '200px', height: 'auto' }} />
              </div>
            )}
          </div>
        ),
        width: 600,
      });
    } catch (err) {
      message.error("Failed to load category details");
    }
  };

  // Upload handler
  const handleUploadChange = ({ fileList: newFileList }) => setFileList(newFileList);

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return false;
    }
    return false;
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setFileList([]);
    setEditingCategory(null);
  };

  // Table columns
  const parentCategoryColumns = [
    { title: 'Category', dataIndex: 'name', key: 'name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Image',
      key: 'image',
      render: (_, record) =>
        record.image?.url ? <img src={record.image.url} width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ color: '#999' }}>No image</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {/* <Button icon={<EyeOutlined />} onClick={() => handleView(record._id)} title="View" /> */}
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} title="Edit" />
          <Popconfirm
            title="Are you sure you want to delete this category?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger title="Delete" />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const subcategoryColumns = [
    { title: 'Category', dataIndex: 'name', key: 'name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Parent',
      key: 'parent',
      render: (_, record) => record.parentCategory?.name || 'None',
    },
    {
      title: 'Image',
      key: 'image',
      render: (_, record) =>
        record.image?.url ? <img src={record.image.url} width={50} height={50} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ color: '#999' }}>No image</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {/* <Button icon={<EyeOutlined />} onClick={() => handleView(record._id)} title="View" /> */}
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} title="Edit" />
          <Popconfirm
            title="Are you sure you want to delete this category?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger title="Delete" />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="category-container">
      <div className="category-header">
        <h2>Category Management</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCategory(null);
            form.resetFields();
            setFileList([]);
            setIsModalOpen(true);
          }}
        >
          Add Category
        </Button>
      </div>

      <div className="table-section">
        <h3>Parent Categories</h3>
        <Table
          columns={parentCategoryColumns}
          dataSource={categories.filter(cat => !cat.parentCategory)}
          rowKey="_id"
          loading={loading}
          pagination={false}
        />
      </div>

      {/* <div className="table-section">
        <h3>Subcategories</h3>
        <Table
          columns={subcategoryColumns}
          dataSource={categories.filter(cat => cat.parentCategory)}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 15 }}
        />
      </div> */}

      <Modal
        open={isModalOpen}
        onCancel={handleModalCancel}
        title={editingCategory ? "Edit Category" : "Add New Category"}
        onOk={() => form.submit()}
        okText={editingCategory ? "Update Category" : "Create Category"}
        confirmLoading={submitLoading}
        destroyOnClose
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <Form.Item
            name="name"
            label="Category Name"
            rules={[
              { required: true, message: 'Please enter category name' },
              { min: 2, message: 'Category name must be at least 2 characters' }
            ]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 500, message: 'Description cannot exceed 500 characters' }]}
          >
            <Input.TextArea rows={3} placeholder="Enter category description (optional)" showCount maxLength={500} />
          </Form.Item>

          <Form.Item name="parentCategory" label="Parent Category">
            <Select allowClear placeholder="Select parent category (optional)">
              {parentCategories.filter(cat => cat._id !== editingCategory?._id).map(cat => (
                <Select.Option key={cat._id} value={cat._id}>{cat.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Category Image">
            <Upload
              fileList={fileList}
              listType="picture-card"
              onChange={handleUploadChange}
              beforeUpload={beforeUpload}
              onRemove={() => setFileList([])}
              accept="image/*"
              maxCount={1}
            >
              {fileList.length === 0 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload Image</div>
                </div>
              )}
            </Upload>
            <p className="upload-info">Max file size: 5MB. Formats: JPG, PNG, GIF, WebP</p>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManager;
